import { apiClient, setAuthToken } from '../../shared/utils/api';
import { extractContractError } from '@bafkal/ui-shared/utils';
import { Auth } from 'aws-amplify';
import cognitoConfig from '../../shared/config/cognitoConfig';
import {
  AUTH_INITIALIZED,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  REGISTER_REQUEST,
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  LOGOUT,
} from '../../store/types';
import { API_ROUTES, UI_MESSAGES } from '../../shared/constants';

// Login action
export const login = (email, password) => async (dispatch) => {
  dispatch({ type: LOGIN_REQUEST });

  try {
    const response = await apiClient.post(API_ROUTES.AUTH.LOGIN, { email, password });
    const { token, user } = response.data;

    // Set the token in the API client (centralized helper)
    setAuthToken(token);

    dispatch({
      type: LOGIN_SUCCESS,
      payload: { token, user },
    });

    return response.data;
  } catch (err) {
    const contractError = extractContractError(err, UI_MESSAGES.AUTH.LOGIN_FAILED);
    dispatch({
      type: LOGIN_FAIL,
      payload: contractError.message,
    });
    return Promise.reject(contractError.message);
  }
};

// Register action
export const register = (formData) => async (dispatch) => {
  dispatch({ type: REGISTER_REQUEST });

  try {
    const response = await apiClient.post(API_ROUTES.AUTH.REGISTER, formData);
    const { token, user } = response.data;

    // Set the token in the API client (centralized helper)
    setAuthToken(token);

    dispatch({
      type: REGISTER_SUCCESS,
      payload: { token, user },
    });

    return response.data;
  } catch (err) {
    const contractError = extractContractError(err, UI_MESSAGES.AUTH.REGISTER_FAILED);
    dispatch({
      type: REGISTER_FAIL,
      payload: contractError.message,
    });
    throw err;
  }
};

// Logout action
export const logout = (options = { hostedLogout: true, silent: false }) => async (dispatch) => {
  const { hostedLogout = true, silent = false } = options;
  if (!silent && process.env.NODE_ENV === 'development') {
    console.info('[auth] logout invoked');
  }

  try {
    // Attempt to sign out locally first
    await Auth.signOut();
  } catch (err) {
    // ignore sign out errors
  }

  // Remove token from the API client and update store
  setAuthToken(null);
  dispatch({ type: LOGOUT });

  // If Hosted UI logout is configured and requested, redirect the browser to Cognito's logout endpoint
  const oauth = cognitoConfig?.Auth?.oauth;
  if (
    hostedLogout &&
    oauth &&
    oauth.domain &&
    oauth.redirectSignOut &&
    cognitoConfig.Auth.userPoolWebClientId
  ) {
    const domain = oauth.domain.replace(/^https?:\/\//, '');
    const logoutUrl = `https://${domain}/logout?client_id=${
      cognitoConfig.Auth.userPoolWebClientId
    }&logout_uri=${encodeURIComponent(oauth.redirectSignOut)}`;

    // Log a helpful warning if the configured logout redirect doesn't match the current origin
    if (!oauth.redirectSignOut.startsWith(window.location.origin)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `Configured logout redirect (${oauth.redirectSignOut}) does not match current origin (${window.location.origin}). If you see an 'Invalid request' error, add the exact logout URL to the Cognito App client's Sign-out URLs.`
        );
      }
    }

    window.location.assign(logoutUrl);
  }
};

// Check for an existing Amplify/Cognito session on app start
export const checkAuth = () => async (dispatch) => {
  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();
    const user = await Auth.currentAuthenticatedUser();

    // Set the token for API requests
    setAuthToken(accessToken);

    dispatch({
      type: LOGIN_SUCCESS,
      payload: {
        token: accessToken,
        user: {
          username: user.username,
          attributes: user.attributes,
        },
      },
    });

    return { token: accessToken, user };
  } catch (err) {
    // Not signed in or session expired; do nothing
    return null;
  } finally {
    // Notify that the initial auth check has completed (success or failure)
    dispatch({ type: AUTH_INITIALIZED });
  }
};